import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { IContentTypeInfo } from '../serverApi/types/IContentTypeInfo';
import { isRelayableItem, relayWireContentType } from './relaySelection';

/**
 * The content types currently supported for adding to a Content Relay plan.
 *
 * The authoritative, growing set is owned by the tabhf-content-mobility service and will be read
 * from its `listSupportedContentTypes` RPC via {@link GetSupportedContentTypesRequest}. That RPC is
 * not yet surfaced through the monolith web client, so for now we work against a SIMULATED list.
 *
 * The set is cached synchronously (seeded with the simulated list) because the Actions-menu gating
 * that consumes it (isClickable) runs synchronously and cannot await a request. {@link loadSupportedContentTypes}
 * refreshes the cache; once the monolith exposes the method, call it at session init so the cache is
 * warm before menus render — the only change needed then is in {@link fetchSupportedContentTypes}.
 */

/** Simulated `listSupportedContentTypes` response — the initial supported set. Edit to experiment. */
const SIMULATED_SUPPORTED_CONTENT_TYPES: ReadonlyArray<IContentTypeInfo> = [
  { contentType: 'PROJECTS', displayName: 'Projects' },
  { contentType: 'WORKBOOKS', displayName: 'Workbooks' },
  { contentType: 'DATASOURCES', displayName: 'Data Sources' },
];

/** Synchronous cache of supported wire content-type keys, seeded with the simulated set. */
let cachedWireTypes: string[] = SIMULATED_SUPPORTED_CONTENT_TYPES.map(function(info) { return info.contentType; });

let loadPromise: Promise<IContentTypeInfo[]>;

/**
 * The single swap point. Today it resolves to the simulated list. When `listSupportedContentTypes`
 * is exposed through the monolith web client, replace the body with:
 *   return WebHttpClient.sendRequest(new GetSupportedContentTypesRequest())
 *     .then(result => result.contentTypes || []);
 */
function fetchSupportedContentTypes(): Promise<IContentTypeInfo[]> {
  return Promise.resolve(SIMULATED_SUPPORTED_CONTENT_TYPES.slice());
}

/** Loads (once) the supported content types and refreshes the synchronous cache. */
export function loadSupportedContentTypes(): Promise<IContentTypeInfo[]> {
  if (!loadPromise) {
    loadPromise = fetchSupportedContentTypes().then(function(types) {
      cachedWireTypes = types.map(function(info) { return info.contentType; });
      return types;
    });
  }
  return loadPromise;
}

/** The currently-known supported wire content-type keys (from cache). */
export function getSupportedWireTypesSync(): ReadonlyArray<string> {
  return cachedWireTypes;
}

/** True when the given wire content-type key is in the supported set. */
export function isWireTypeSupported(wireContentType: string): boolean {
  return !!wireContentType && cachedWireTypes.indexOf(wireContentType) !== -1;
}

/**
 * True when the item can be added to a relay plan right now: it is a client-relayable type and its
 * content type is in the currently-supported set. Layers the dynamic supported set over the static
 * {@link isRelayableItem} mapping.
 */
export function isItemSupportedForRelay(item: IContentInfo): boolean {
  return isRelayableItem(item) && isWireTypeSupported(relayWireContentType(item.contentType));
}
